<template>
  <div class="restaurant-settings-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="restaurant-settings-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="title-icon">
                  <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                </svg>
                Restaurant Settings
              </h1>
              <p class="page-subtitle">Configure your WhatsApp restaurant ordering system</p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading settings...</p>
          </div>

          <!-- Settings Form -->
          <div v-else class="settings-form">
            <!-- Basic Information Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
                Basic Information
              </h3>

              <!-- Logo Upload -->
              <div class="form-group logo-group">
                <label class="form-label">Restaurant Logo</label>
                <div class="logo-upload-area">
                  <div v-if="settings.logo_url" class="logo-preview">
                    <img :src="settings.logo_url" alt="Restaurant Logo" class="logo-img" />
                    <button type="button" class="logo-remove-btn" @click="settings.logo_url = null" title="Remove logo">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                      </svg>
                    </button>
                  </div>
                  <div v-else class="logo-placeholder">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="color:#94a3b8">
                      <path d="M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19M8.5,13.5L11,16.51L14.5,12L19,18H5L8.5,13.5Z"/>
                    </svg>
                    <span>No logo uploaded</span>
                  </div>
                  <div class="logo-actions">
                    <label class="logo-upload-btn" :class="{ 'is-loading': logoUploading }">
                      <input type="file" accept="image/*" @change="uploadLogo" style="display:none" :disabled="logoUploading" />
                      <div v-if="logoUploading" class="button-spinner" style="border-color:rgba(59,130,246,.3);border-top-color:#3b82f6"></div>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
                      </svg>
                      {{ logoUploading ? 'Uploading…' : (settings.logo_url ? 'Replace Logo' : 'Upload Logo') }}
                    </label>
                    <span class="logo-hint">JPG, PNG, WebP · Max 5 MB · Shown in mini-app</span>
                  </div>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Restaurant Name (English) *</label>
                  <input
                    v-model="settings.restaurant_name"
                    type="text"
                    placeholder="e.g., Pizza Palace"
                    class="form-input"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Restaurant Name (Arabic)</label>
                  <input
                    v-model="settings.restaurant_name_ar"
                    type="text"
                    placeholder="e.g., بيتزا بالاس"
                    class="form-input"
                    dir="rtl"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Description (English)</label>
                  <textarea
                    v-model="settings.restaurant_description"
                    placeholder="Brief description of your restaurant..."
                    class="form-textarea"
                    rows="3"
                  ></textarea>
                </div>

                <div class="form-group">
                  <label class="form-label">Description (Arabic)</label>
                  <textarea
                    v-model="settings.restaurant_description_ar"
                    placeholder="وصف موجز لمطعمك..."
                    class="form-textarea"
                    rows="3"
                    dir="rtl"
                  ></textarea>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Currency</label>
                <select v-model="settings.currency" class="form-input">
                  <option value="EGP">EGP — Egyptian Pound</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="SAR">SAR — Saudi Riyal</option>
                  <option value="AED">AED — UAE Dirham</option>
                  <option value="GBP">GBP — British Pound</option>
                </select>
              </div>
            </div>

            <!-- Delivery Settings Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18,18.5A1.5,1.5 0 0,1 16.5,17A1.5,1.5 0 0,1 18,15.5A1.5,1.5 0 0,1 19.5,17A1.5,1.5 0 0,1 18,18.5M19.5,9.5L21.46,12H17V9.5M6,18.5A1.5,1.5 0 0,1 4.5,17A1.5,1.5 0 0,1 6,15.5A1.5,1.5 0 0,1 7.5,17A1.5,1.5 0 0,1 6,18.5M20,8H17V4H3C1.89,4 1,4.89 1,6V17H3A3,3 0 0,0 6,20A3,3 0 0,0 9,17H15A3,3 0 0,0 18,20A3,3 0 0,0 21,17H23V12L20,8Z"/>
                </svg>
                Delivery Settings
              </h3>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Enable Delivery</span>
                    <span class="toggle-description">Allow customers to place delivery orders</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.delivery_enabled"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Enable Pickup</span>
                    <span class="toggle-description">Allow customers to pick up orders from your location</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.pickup_enabled"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Max Active Orders Per Customer</label>
                  <input
                    v-model.number="settings.max_active_orders"
                    type="number"
                    min="0"
                    max="99"
                    class="form-input"
                  />
                  <small class="form-hint">Limit how many unfinished orders a customer can have at once. Set to 0 for unlimited.</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Delivery Fee (EGP)</label>
                  <input
                    v-model.number="settings.delivery_fee"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    class="form-input"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Minimum Order Amount (EGP)</label>
                  <input
                    v-model.number="settings.min_order_amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    class="form-input"
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Cart Expiry Time</label>
                <select v-model.number="settings.cart_expiry_minutes" class="form-select">
                  <option :value="30">30 Minutes</option>
                  <option :value="60">60 Minutes</option>
                  <option :value="90">90 Minutes</option>
                </select>
                <small class="form-hint">How long items stay in customer's cart</small>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Estimated Delivery Time (min)</label>
                  <input
                    v-model.number="settings.delivery_eta_minutes"
                    type="number"
                    min="0"
                    placeholder="30"
                    class="form-input"
                  />
                  <small class="form-hint">Shown to customers at checkout. 0 = hide ETA</small>
                </div>

                <div class="form-group">
                  <label class="form-label">Cancellation Window (min)</label>
                  <input
                    v-model.number="settings.cancel_window_minutes"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="form-input"
                  />
                  <small class="form-hint">How long customers can cancel after ordering. 0 = not allowed</small>
                </div>

                <div class="form-group">
                  <label class="form-label">Rating Request Delay (min)</label>
                  <input
                    v-model.number="settings.rating_request_delay_minutes"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="form-input"
                  />
                  <small class="form-hint">Send a rating request to the customer this many minutes after delivery. 0 = send immediately</small>
                </div>
              </div>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Send Receipt Image on Delivery</span>
                    <span class="toggle-description">Automatically send a receipt image to customers when order is delivered</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.send_receipt_on_delivery"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Operating Hours Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                </svg>
                Operating Hours
              </h3>
              <p style="font-size:13px;color:var(--text-secondary);margin-bottom:14px;">
                Set your open/close times per day. Customers will see a "closed" notice outside these hours.
              </p>

              <table class="hours-table">
                <thead>
                  <tr>
                    <th class="hours-th">Day</th>
                    <th class="hours-th" style="text-align:center">Open</th>
                    <th class="hours-th">Opens</th>
                    <th class="hours-th">Closes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="h in hours"
                    :key="h.day_of_week"
                    class="hours-row"
                    :class="{ 'hours-row-closed': h.is_closed }"
                  >
                    <td class="hours-day-label">{{ ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][h.day_of_week] }}</td>
                    <td class="hours-td-center">
                      <label class="toggle-switch" style="margin:0 auto">
                        <input type="checkbox" :checked="!h.is_closed" @change="h.is_closed = !$event.target.checked" class="toggle-input" />
                        <span class="toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <input
                        type="time"
                        v-model="h.open_time"
                        class="hours-time-input"
                        :disabled="!!h.is_closed"
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        v-model="h.close_time"
                        class="hours-time-input"
                        :disabled="!!h.is_closed"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

            </div>

            <!-- Branch Settings Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z"/>
                </svg>
                Branch Settings
              </h3>

              <div class="form-group">
                <div class="toggle-group">
                  <div class="toggle-label-text">
                    <span class="toggle-title">Enable Multi-Branch Mode</span>
                    <span class="toggle-description">Uses your departments as restaurant branches</span>
                  </div>
                  <label class="toggle-switch">
                    <input
                      v-model="settings.multi_branch_enabled"
                      type="checkbox"
                      class="toggle-input"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Welcome Messages Card -->
            <div class="settings-card">
              <h3 class="card-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,3C6.5,3 2,6.58 2,11A7.17,7.17 0 0,0 4.22,16.22C3.25,18.22 2,18.73 2,18.73C2,18.73 6.16,18.93 9.37,17.36C10.23,17.61 11.11,17.73 12,17.73C17.5,17.73 22,14.15 22,9.73C22,5.31 17.5,3 12,3M12,15.73C7.5,15.73 4,12.84 4,9.23C4,5.62 7.5,3 12,3C16.5,3 20,5.88 20,9.5C20,13.12 16.5,15.73 12,15.73Z"/>
                </svg>
                Welcome Messages
              </h3>

              <div class="form-group">
                <label class="form-label">Welcome Message (English)</label>
                <textarea
                  v-model="settings.welcome_message"
                  placeholder="Welcome to our restaurant! Browse our menu and order easily."
                  class="form-textarea"
                  rows="3"
                ></textarea>
              </div>

              <div class="form-group">
                <label class="form-label">Welcome Message (Arabic)</label>
                <textarea
                  v-model="settings.welcome_message_ar"
                  placeholder="مرحباً بك في مطعمنا! تصفح قائمتنا واطلب بسهولة."
                  class="form-textarea"
                  rows="3"
                  dir="rtl"
                ></textarea>
              </div>
            </div>

            <!-- Rider Dispatch Settings -->
            <div class="settings-card">
              <div class="card-header">
                <h3 class="card-title">🛵 Rider Dispatch Settings</h3>
                <p class="card-subtitle">Configure how new orders are dispatched to your rider pool via Telegram group.</p>
              </div>

              <div class="form-row">
                <div class="form-group full-width">
                  <label class="form-label">Rider Group Chat ID</label>
                  <input
                    v-model="settings.rider_group_chat_id"
                    type="text"
                    class="form-input"
                    placeholder="-1001234567890"
                  />
                  <p class="form-hint">
                    The Telegram group where riders receive order notifications.
                    <a href="https://api.quali-d.com/restaurant/rider-setup-guide" target="_blank" style="color:#4f46e5;font-weight:600;">How to get this →</a>
                  </p>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Priority Window (minutes)</label>
                  <input
                    v-model.number="settings.rider_priority_window_minutes"
                    type="number" min="1" max="15"
                    class="form-input"
                  />
                  <p class="form-hint">Riders who recently delivered must wait this long before claiming a new order (gives others a fair chance).</p>
                </div>
                <div class="form-group">
                  <label class="form-label">Cooldown After Delivery (minutes)</label>
                  <input
                    v-model.number="settings.rider_cooldown_minutes"
                    type="number" min="1" max="60"
                    class="form-input"
                  />
                  <p class="form-hint">How long a rider waits after delivering before they get first-pick again.</p>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Daily Order Limit Per Rider</label>
                  <input
                    v-model.number="settings.rider_daily_limit"
                    type="number" min="1" max="50"
                    class="form-input"
                  />
                  <p class="form-hint">Max deliveries one rider can accept in a single day (0 = no limit).</p>
                </div>
              </div>

              <div class="info-box">
                <strong>How it works:</strong> When an order comes in, all riders in the group see it at once.
                Riders who haven't recently delivered get first claim. After {{ settings.rider_priority_window_minutes }} min,
                everyone can accept. First to tap wins. 🏆
              </div>
            </div>

            <!-- App Theme / Colour Palette -->
            <div class="settings-card">
              <div class="card-header">
                <h3 class="card-title">🎨 App Colour Theme</h3>
                <p class="card-subtitle">Choose a brand colour for your ordering mini-app. Buttons, prices, and accents will use this colour.</p>
              </div>

              <div class="palette-grid">
                <button
                  v-for="p in palettes"
                  :key="p.id"
                  type="button"
                  :class="['palette-swatch', { selected: settings.theme_accent === p.accent }]"
                  :style="p.accent ? { background: p.accent } : {}"
                  :title="p.name"
                  @click="settings.theme_accent = p.accent"
                >
                  <span v-if="!p.accent" class="swatch-default-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 2v20M2 12h20"/></svg>
                  </span>
                  <span v-if="settings.theme_accent === p.accent" class="swatch-check">✓</span>
                </button>
              </div>

              <div class="palette-labels">
                <span
                  v-for="p in palettes"
                  :key="'lbl-' + p.id"
                  :class="['palette-label', { selected: settings.theme_accent === p.accent }]"
                >{{ p.name }}</span>
              </div>

              <div v-if="settings.theme_accent" class="theme-preview">
                <div class="preview-bar" :style="{ background: settings.theme_accent }">
                  <span :style="{ color: accentTextColor }">Preview — Add to Cart &nbsp; 🛒</span>
                </div>
                <p class="form-hint" style="margin-top:8px">This is how buttons will look in the customer mini-app.</p>
              </div>
            </div>

            <!-- Language Settings -->
            <div class="settings-card">
              <div class="card-header">
                <h3 class="card-title">🌐 Language Settings</h3>
                <p class="card-subtitle">Control which languages customers can use in the mini-app and the language for rider notifications.</p>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Customer App Languages</label>
                  <div class="radio-group">
                    <label class="radio-option" :class="{ active: settings.supported_languages === 'both' }">
                      <input type="radio" v-model="settings.supported_languages" value="both" />
                      <span class="radio-label">English &amp; Arabic</span>
                      <span class="radio-sub">Customer can switch between both languages</span>
                    </label>
                    <label class="radio-option" :class="{ active: settings.supported_languages === 'en' }">
                      <input type="radio" v-model="settings.supported_languages" value="en" />
                      <span class="radio-label">English only</span>
                      <span class="radio-sub">Mini-app always shows in English</span>
                    </label>
                    <label class="radio-option" :class="{ active: settings.supported_languages === 'ar' }">
                      <input type="radio" v-model="settings.supported_languages" value="ar" />
                      <span class="radio-label">Arabic only</span>
                      <span class="radio-sub">Mini-app always shows in Arabic</span>
                    </label>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">Rider Notification Language</label>
                  <div class="radio-group">
                    <label class="radio-option" :class="{ active: settings.rider_language === 'en' }">
                      <input type="radio" v-model="settings.rider_language" value="en" />
                      <span class="radio-label">English</span>
                      <span class="radio-sub">Group messages and DMs sent in English</span>
                    </label>
                    <label class="radio-option" :class="{ active: settings.rider_language === 'ar' }">
                      <input type="radio" v-model="settings.rider_language" value="ar" />
                      <span class="radio-label">Arabic</span>
                      <span class="radio-sub">Group messages and DMs sent in Arabic</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Save Button -->
            <div class="form-actions">
              <button @click="saveSettings" class="btn-primary" :disabled="saving">
                <svg v-if="!saving" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z"/>
                </svg>
                <div v-else class="button-spinner"></div>
                {{ saving ? 'Saving...' : 'Save Settings' }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Unsaved Changes Bar -->
    <transition name="slide-up">
      <div v-if="hasUnsavedChanges && !loading" class="unsaved-bar">
        <div class="unsaved-bar-content">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
          </svg>
          <span>You have unsaved changes &mdash; scroll down to save</span>
        </div>
        <button @click="saveSettings" :disabled="saving" class="unsaved-bar-btn">
          <div v-if="saving" class="button-spinner unsaved-spinner"></div>
          {{ saving ? 'Saving...' : 'Save Now' }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'

const PALETTES = [
  { id: 'default', name: 'Telegram Default', accent: null },
  { id: 'flame', name: 'Flame', accent: '#FF6B35' },
  { id: 'tomato', name: 'Tomato', accent: '#E63946' },
  { id: 'rose', name: 'Rose', accent: '#E91E63' },
  { id: 'violet', name: 'Violet', accent: '#7B2FBE' },
  { id: 'ocean', name: 'Ocean', accent: '#0077B6' },
  { id: 'teal', name: 'Teal', accent: '#0D9488' },
  { id: 'forest', name: 'Forest', accent: '#2D6A4F' },
  { id: 'olive', name: 'Olive', accent: '#606C38' },
  { id: 'espresso', name: 'Espresso', accent: '#6F4E37' },
  { id: 'saffron', name: 'Saffron', accent: '#F59E0B' },
  { id: 'gold', name: 'Gold', accent: '#D97706' },
  { id: 'slate', name: 'Slate', accent: '#475569' },
  { id: 'midnight', name: 'Midnight', accent: '#1E1B4B' },
  { id: 'black', name: 'Black', accent: '#111111' }
]

export default {
  name: 'RestaurantSettingsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: false,
      saving: false,
      palettes: PALETTES,
      logoUploading: false,
      savedSettings: null,
      savedHours: null,
      settings: {
        restaurant_name: '',
        restaurant_name_ar: '',
        restaurant_description: '',
        restaurant_description_ar: '',
        delivery_enabled: true,
        pickup_enabled: true,
        max_active_orders: 1,
        delivery_fee: 0,
        min_order_amount: 0,
        cart_expiry_minutes: 30,
        send_receipt_on_delivery: false,
        multi_branch_enabled: false,
        welcome_message: '',
        welcome_message_ar: '',
        logo_url: null,
        rider_group_chat_id: '',
        rider_cooldown_minutes: 10,
        rider_priority_window_minutes: 3,
        rider_daily_limit: 15,
        theme_accent: null,
        supported_languages: 'both',
        rider_language: 'en',
        delivery_eta_minutes: 30,
        cancel_window_minutes: 0,
        currency: 'EGP',
        rating_request_delay_minutes: 0
      },
      hours: [
        { day_of_week: 0, open_time: '09:00', close_time: '22:00', is_closed: false },
        { day_of_week: 1, open_time: '09:00', close_time: '22:00', is_closed: false },
        { day_of_week: 2, open_time: '09:00', close_time: '22:00', is_closed: false },
        { day_of_week: 3, open_time: '09:00', close_time: '22:00', is_closed: false },
        { day_of_week: 4, open_time: '09:00', close_time: '22:00', is_closed: false },
        { day_of_week: 5, open_time: '09:00', close_time: '22:00', is_closed: false },
        { day_of_week: 6, open_time: '09:00', close_time: '22:00', is_closed: false }
      ]
    }
  },
  computed: {
    hasUnsavedChanges () {
      if (!this.savedSettings) return false
      const settingsChanged = JSON.stringify(this.settings) !== JSON.stringify(this.savedSettings)
      const hoursChanged = this.savedHours ? JSON.stringify(this.hours) !== JSON.stringify(this.savedHours) : false
      return settingsChanged || hoursChanged
    },
    accentTextColor () {
      const hex = this.settings.theme_accent
      if (!hex) return '#ffffff'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return (r * 299 + g * 587 + b * 114) / 1000 > 140 ? '#111111' : '#ffffff'
    }
  },
  async mounted () {
    // Ensure restaurant mode is set in authStore when this page loads
    await this.checkRestaurantMode()
    this.loadSettings()
    this.loadHours()
  },
  methods: {
    async checkRestaurantMode () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant/mode/check', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          authStore.setRestaurantMode(data.restaurant_mode || false)
        }
      } catch (error) {
        console.error('Error checking restaurant mode:', error)
      }
    },

    async loadSettings () {
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/settings', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success && data.settings) {
          // Convert integer 1/0 to boolean true/false for toggle switches
          const settings = {
            ...data.settings,
            delivery_enabled: Boolean(data.settings.delivery_enabled),
            pickup_enabled: data.settings.pickup_enabled !== undefined ? Boolean(data.settings.pickup_enabled) : true,
            max_active_orders: data.settings.max_active_orders !== undefined ? parseInt(data.settings.max_active_orders, 10) : 1,
            send_receipt_on_delivery: Boolean(data.settings.send_receipt_on_delivery),
            multi_branch_enabled: Boolean(data.settings.multi_branch_enabled)
          }
          this.settings = { ...this.settings, ...settings }
          this.savedSettings = JSON.parse(JSON.stringify(this.settings))
        }
      } catch (error) {
        console.error('Error loading settings:', error)
        alert('Failed to load settings')
      } finally {
        this.loading = false
      }
    },

    async uploadLogo (event) {
      const file = event.target.files[0]
      if (!file) return
      this.logoUploading = true
      try {
        const token = localStorage.getItem('auth_token')
        const formData = new FormData()
        formData.append('logo', file)
        const response = await fetch('https://api.quali-d.com/restaurant-mode/upload-logo', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        })
        const data = await response.json()
        if (data.success) {
          this.settings.logo_url = data.logo_url
        } else {
          alert('Upload failed: ' + (data.message || 'Unknown error'))
        }
      } catch (e) {
        alert('Upload failed')
      } finally {
        this.logoUploading = false
        event.target.value = ''
      }
    },

    async saveSettings () {
      try {
        this.saving = true
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/settings', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.settings)
        })

        const data = await response.json()
        if (data.success) {
          this.savedSettings = JSON.parse(JSON.stringify(this.settings))

          // Save operating hours alongside settings
          const hoursResponse = await fetch('https://api.quali-d.com/restaurant-mode/working-hours', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ hours: this.hours })
          })
          const hoursData = await hoursResponse.json()
          if (hoursData.success) {
            this.savedHours = JSON.parse(JSON.stringify(this.hours))
          }

          alert('Settings saved successfully!')
        } else {
          alert('Failed to save settings: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error saving settings:', error)
        alert('Failed to save settings')
      } finally {
        this.saving = false
      }
    },

    async loadHours () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/working-hours', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success && data.hours) {
          this.hours = data.hours.map(h => ({
            ...h,
            is_closed: Boolean(h.is_closed)
          }))
          this.savedHours = JSON.parse(JSON.stringify(this.hours))
        }
      } catch (e) {
        console.error('Error loading hours:', e)
      }
    }
  }
}
</script>

<style scoped>
.restaurant-settings-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.restaurant-settings-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Settings Form */
.settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.settings-card {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
}

.card-title {
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--color-border);
}

/* Form Elements */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  font-family: inherit;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

/* ── Colour Palette Picker ── */
.palette-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}
.palette-swatch {
  width: 44px; height: 44px;
  border-radius: 12px;
  border: 2.5px solid transparent;
  cursor: pointer;
  position: relative;
  display: flex; align-items: center; justify-content: center;
  background: var(--bg-secondary);
  transition: transform .15s, border-color .15s;
  flex-shrink: 0;
}
.palette-swatch:hover { transform: scale(1.1); }
.palette-swatch.selected { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.25); }
.swatch-check {
  color: #fff; font-size: 18px; font-weight: 900;
  text-shadow: 0 1px 3px rgba(0,0,0,.5);
  pointer-events: none;
}
.swatch-default-icon { color: #94a3b8; pointer-events: none; }
.palette-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 6px;
}
.palette-label {
  width: 44px;
  font-size: 10px;
  color: var(--text-secondary);
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.palette-label.selected { color: #4f46e5; font-weight: 700; }
.theme-preview {
  margin-top: 16px;
}
.preview-bar {
  border-radius: 10px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 700;
  display: inline-block;
}

/* ── Language Radio Options ── */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.radio-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  border: 1.5px solid var(--border-primary);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  position: relative;
}
.radio-option input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.radio-option:hover { border-color: #3b82f6; }
.radio-option.active {
  border-color: #3b82f6;
  background: rgba(59,130,246,.06);
}
.radio-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}
.radio-sub {
  font-size: 12px;
  color: var(--text-secondary);
}

/* ── Operating Hours Table ── */
.hours-table {
  width: 100%;
  border-collapse: collapse;
}
.hours-th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  padding: 6px 10px 10px;
  text-transform: uppercase;
  letter-spacing: .05em;
}
.hours-row {
  border-top: 1px solid var(--border-primary);
}
.hours-row-closed td {
  opacity: 0.45;
}
.hours-row-closed td:nth-child(2) {
  opacity: 1;
}
.hours-day-label {
  padding: 10px 10px;
  font-weight: 600;
  font-size: 14px;
  width: 50px;
}
.hours-td-center {
  padding: 10px;
  text-align: center;
}
.hours-time-input {
  padding: 6px 10px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
}
.hours-time-input:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Toggle Switch Styles */
.toggle-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: var(--bg-secondary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.toggle-group:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.toggle-label-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.toggle-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.toggle-description {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 400;
}

/* Toggle Switch Component */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 72px;
  height: 40px;
  flex-shrink: 0;
  cursor: pointer;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  border-radius: 40px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-slider:before {
  content: "";
  position: absolute;
  height: 32px;
  width: 32px;
  left: 4px;
  top: 4px;
  background: white;
  border-radius: 50%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.toggle-input:checked + .toggle-slider {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(59, 130, 246, 0.4),
    0 0 0 4px rgba(59, 130, 246, 0.1);
}

.toggle-input:checked + .toggle-slider:before {
  transform: translateX(32px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.toggle-switch:hover .toggle-slider {
  transform: scale(1.02);
}

.toggle-input:focus + .toggle-slider {
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 0 0 4px rgba(59, 130, 246, 0.2);
}

.toggle-input:checked:focus + .toggle-slider {
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(59, 130, 246, 0.4),
    0 0 0 4px rgba(59, 130, 246, 0.3);
}

/* Dark mode support for toggles */
@media (prefers-color-scheme: dark) {
  .toggle-slider {
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
  }

  .toggle-input:checked + .toggle-slider {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  }
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    padding: 16px;
  }

  .header-content {
    padding: 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .settings-card {
    padding: 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .toggle-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
  }

  .toggle-label-text {
    width: 100%;
  }

  .toggle-switch {
    align-self: flex-end;
  }
}

/* Logo Upload */
.logo-group { margin-bottom: 24px; }

.logo-upload-area {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 16px;
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  background: var(--bg-secondary);
}

.logo-preview {
  position: relative;
  flex-shrink: 0;
}
.logo-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid var(--border-primary);
  background: var(--bg-primary);
}
.logo-remove-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s;
}
.logo-remove-btn:hover { background: #dc2626; }

.logo-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  border: 2px dashed var(--border-primary);
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  color: var(--text-secondary);
  font-size: 10px;
  text-align: center;
  padding: 4px;
}

.logo-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.logo-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  user-select: none;
}
.logo-upload-btn:hover { background: #2563eb; }
.logo-upload-btn.is-loading { opacity: 0.75; cursor: not-allowed; }
.logo-hint {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Unsaved Changes Bar */
.unsaved-bar {
  position: fixed;
  bottom: 0;
  left: 280px;
  right: 0;
  background: #f59e0b;
  color: #fff;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  z-index: 200;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
}

.unsaved-bar-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
}

.unsaved-bar-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #fff;
  color: #d97706;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.unsaved-bar-btn:hover:not(:disabled) {
  background: #fef3c7;
}

.unsaved-bar-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.unsaved-spinner {
  border-color: rgba(217, 119, 6, 0.3);
  border-top-color: #d97706;
}

.info-box {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 13px;
  color: #1e40af;
  line-height: 1.6;
  margin-top: 8px;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

@media (max-width: 768px) {
  .unsaved-bar {
    left: 0;
    flex-wrap: wrap;
  }
}
</style>
